import React from "react";
import { useIsAuthenticated, useMsal } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Navigate, useLocation } from "react-router-dom";
import { redirectToLoginWithError, storePostLoginRedirect } from "../utils/authRedirect";
import { useAuthInfo } from "../context/AuthInfoContext";
import { ROUTE_KEYS } from "../config/routeKeys";
import { AUTH_VEREIST, GEEN_TOEGANG_PAGINA, OPNIEUW_PROBEREN } from "../utils/authMessages";
import { authGateDecision } from "../utils/authGate";
import Spinner from "./common/Spinner";
import Alert from "./common/Alert";
import WaitingForAccess from "./WaitingForAccess";
import ActivationScreen from "./ActivationScreen";
import { firstLanding, pendingActivations } from "../utils/landing";

// Gedeelde wachtstaat: MSAL is nog bezig, we weten nog niet of er een sessie is.
function AuthPending() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: "12px",
        minHeight: "60vh",
        color: "#64748b",
      }}
    >
      <Spinner size={28} />
      <span style={{ fontSize: "14px" }}>Bezig met aanmelden…</span>
    </div>
  );
}

// De login start op /login, maar redirectUri is de origin (/). msal-browser
// cachet de hash daarom onder `msal.<clientId>.urlHash` en doet
// window.location.replace("/login"). In die tussenliggende page-load is er al
// een account, maar useIsAuthenticated() geeft tijdens Startup hard false
// terug. Schrijven we dán POST_LOGIN_REDIRECT, dan overschrijven we de echte
// bestemming met "/" of "/login" en navigeert de app na de login naar zichzelf.
function isRedirectInFlight() {
  try {
    if (typeof window === "undefined") return false;
    if (/[#&](code|state|error|id_token)=/.test(window.location.hash || "")) return true;
    // Na de replace() is de hash uit de URL verdwenen, maar de gecachte entry
    // staat er nog tot handleRedirectPromise() hem verwerkt.
    for (let i = 0; i < sessionStorage.length; i++) {
      const key = sessionStorage.key(i);
      if (key && key.startsWith("msal.") && key.endsWith(".urlHash")) return true;
    }
    return false;
  } catch {
    return false;
  }
}

const ProtectedRoute = ({ children }) => {
  const isAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const location = useLocation();

  // Tijdens Startup/redirect-verwerking is isAuthenticated onbetrouwbaar:
  // niets wegschrijven en niet redirecten, maar wachten.
  const bootstrapping =
    (inProgress && inProgress !== InteractionStatus.None) || isRedirectInFlight();
  const shouldCapture = !isAuthenticated && !bootstrapping;

  // Side-effect hoort in een effect, niet in de render-body (draaide daar bij
  // elke render, en dubbel onder StrictMode).
  React.useEffect(() => {
    if (!shouldCapture) return;
    try {
      storePostLoginRedirect(location.pathname + location.search);
      sessionStorage.setItem("AUTH_ERROR_MSG", AUTH_VEREIST);
    } catch {}
  }, [shouldCapture, location.pathname, location.search]);

  // Volgorde is dragend: bootstrapping moet vóór de !isAuthenticated-return,
  // anders keert de redirect tijdens Startup terug.
  if (bootstrapping) {
    return <AuthPending />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  // Server-side confirmation: call /api/auth/me before rendering
  return <ServerAuthGate>{children}</ServerAuthGate>;
};

export default ProtectedRoute;

function ServerAuthGate({ children }) {
  // Central auth/me call gebeurt nu uitsluitend in AuthInfoContext.
  // Deze gate leest alleen de gedeelde state om flicker en dubbele requests te voorkomen.
  const authInfo = useAuthInfo();
  const { error: authError, refresh } = authInfo;
  const location = useLocation();

  // Derive route key mapping — moet meebewegen met de actieve route,
  // dus gebruik react-router's location (niet het bevroren window.location).
  const routeKey = React.useMemo(() => ROUTE_KEYS[location.pathname] || null, [location.pathname]);
  const decision = authGateDecision(authInfo, routeKey);

  if (decision === "pending") {
    return <AuthPending />;
  }
  // Technische fout: in-place tonen met retry. Niet redirecten — dat tikt de
  // anti-loop-teller aan en duwt de gebruiker bij twee storingen binnen 60s in
  // de doodlopende "Aanmelden lukt niet"-staat.
  if (decision === "error") {
    return (
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: "12px",
          minHeight: "60vh",
          padding: "24px",
        }}
      >
        <Alert type="error" style={{ maxWidth: "440px" }}>
          {authError.message}
        </Alert>
        <button type="button" className="btn" onClick={refresh}>
          {OPNIEUW_PROBEREN}
        </button>
      </div>
    );
  }
  // Issue #23: aangemeld zonder rol = wachten op Beheer, geen weigering. In-place tonen; een redirect naar
  // /login stuurde de gebruiker daar meteen terug (lus). Token- of tenantfouten blijven "denied".
  if (decision === "waiting") {
    return <WaitingForAccess />;
  }
  if (decision === "denied") {
    redirectToLoginWithError(GEEN_TOEGANG_PAGINA);
    return <Navigate to="/login" replace />;
  }
  // Issue #23 (landing): enkel activeerbare rollen die nu niet lopen (bv. alleen Beheer) = activatiescherm in plaats
  // van een pagina die 403 geeft en naar "Geen toegang" stuurt.
  if (decision === "activate") {
    return <ActivationScreen />;
  }
  // Pagina niet toegelaten, of het Overzicht zonder actieve bedrijfsrol: door naar de eerste pagina die de gebruiker
  // wel mag zien (menuvolgorde). Is die er niet, dan het activatiescherm of (forbidden) de weigerpagina.
  if (decision === "forbidden" || decision === "landing") {
    const target = firstLanding(authInfo);
    if (target && target.path !== location.pathname) return <Navigate to={target.path} replace />;
    if (pendingActivations(authInfo.access).length > 0) return <ActivationScreen />;
    if (decision === "forbidden") return <Navigate to="/forbidden" replace />;
  }
  return children;
}
